import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const btn = "inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-4 py-2 transition-colors disabled:opacity-60";
const EMPTY = { q: "", a: "", q_en: "", a_en: "" };

export function FaqAdmin({ endpoint = "/admin/documents/faq", heading = "FAQ na stronie dokumentów", description = "Pytania i odpowiedzi widoczne pod poradnikiem na /dokumenty. Wersja angielska jest opcjonalna (gdy pusta, pokazujemy polską).", max = 12 }) {
  const [faq, setFaq] = useState(null);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(endpoint).then((r) => { setFaq(r.data); setSaved(JSON.stringify(r.data)); }).catch(() => toast.error("Nie udało się pobrać FAQ."));
  }, [endpoint]);
  if (!faq) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-600" /></div>;

  const dirty = JSON.stringify(faq) !== saved;
  const setItem = (i, patch) => setFaq({ ...faq, items: faq.items.map((it, j) => (j === i ? { ...it, ...patch } : it)) });
  const move = (i, d) => {
    const items = [...faq.items];
    const [it] = items.splice(i, 1);
    items.splice(i + d, 0, it);
    setFaq({ ...faq, items });
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put(endpoint, faq);
      setSaved(JSON.stringify(faq));
      toast.success("FAQ zapisane.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Nie udało się zapisać FAQ.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="faq-admin" className="bg-card border border-border rounded-2xl p-5 sm:p-6 space-y-5">
      <div>
        <h2 className="font-display font-bold text-lg">{heading}</h2>
        <p className="text-sm text-muted-foreground mt-1">{description}</p>
        <p className="text-xs text-muted-foreground mt-2">Linki w odpowiedziach: wpisz <code className="font-mono text-foreground">[tekst](/biuro-wirtualne-warszawa/dokumenty)</code> lub <code className="font-mono text-foreground">[tekst](#biuro-wirtualne-cennik)</code>. Frazy „generator dokumentów” i „Zamów pakiet” stają się linkami automatycznie.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Input data-testid="faq-title-input" value={faq.title} onChange={(e) => setFaq({ ...faq, title: e.target.value })} placeholder="Tytuł sekcji (PL)" className="bg-background" />
        <Input data-testid="faq-title-en-input" value={faq.title_en || ""} onChange={(e) => setFaq({ ...faq, title_en: e.target.value })} placeholder="Tytuł sekcji (EN)" className="bg-background" />
      </div>
      <div className="space-y-4">
        {faq.items.map((it, i) => (
          <div key={i} data-testid={`faq-item-${i}`} className="rounded-xl border border-border p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Pytanie {i + 1}</span>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 rounded-full hover:bg-muted disabled:opacity-30" aria-label="W górę"><ArrowUp size={13} /></button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === faq.items.length - 1} className="p-1.5 rounded-full hover:bg-muted disabled:opacity-30" aria-label="W dół"><ArrowDown size={13} /></button>
                <button type="button" data-testid={`faq-item-delete-${i}`} onClick={() => setFaq({ ...faq, items: faq.items.filter((_, j) => j !== i) })} className="p-1.5 rounded-full hover:bg-destructive/10 text-destructive" aria-label="Usuń"><Trash2 size={13} /></button>
              </div>
            </div>
            <div className="grid lg:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Input data-testid={`faq-item-q-${i}`} value={it.q} onChange={(e) => setItem(i, { q: e.target.value })} placeholder="Pytanie (PL) *" className="bg-background" />
                <Textarea data-testid={`faq-item-a-${i}`} value={it.a} onChange={(e) => setItem(i, { a: e.target.value })} placeholder="Odpowiedź (PL) *" className="bg-background min-h-[90px] text-sm" />
              </div>
              <div className="space-y-2">
                <Input value={it.q_en || ""} onChange={(e) => setItem(i, { q_en: e.target.value })} placeholder="Question (EN)" className="bg-background" />
                <Textarea value={it.a_en || ""} onChange={(e) => setItem(i, { a_en: e.target.value })} placeholder="Answer (EN)" className="bg-background min-h-[90px] text-sm" />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" data-testid="faq-add-button" onClick={() => setFaq({ ...faq, items: [...faq.items, EMPTY] })} disabled={faq.items.length >= max} className={`${btn} border border-dashed border-border hover:bg-muted`}>
          <Plus size={13} /> Dodaj pytanie
        </button>
        <div className="flex items-center gap-3">
          {dirty && <span className="text-xs text-brand-yellow">Niezapisane zmiany</span>}
          <button type="button" data-testid="faq-save-button" onClick={save} disabled={saving || !dirty} className={`${btn} bg-brand-teal text-white hover:bg-brand-tealDark`}>
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Zapisz FAQ
          </button>
        </div>
      </div>
    </div>
  );
}
